/**
 * Arabic catalog for the inventory and accounting views.
 *
 * Dynamic replacements intentionally preserve captured account/item names, codes,
 * dates, quantities, monetary values, currencies, references, and other user data.
 */
export const inventoryAccountingTranslations: Record<string, string> = {
  // Shared actions, states, fields, and table labels
  'All': 'الكل',
  'Add': 'إضافة',
  'Add New': 'إضافة جديد',
  'Apply': 'تطبيق',
  'Archive': 'أرشفة',
  'Archived': 'مؤرشف',
  'Cancel': 'إلغاء',
  'Close': 'إغلاق',
  'Create': 'إنشاء',
  'Delete': 'حذف',
  'Details': 'التفاصيل',
  'Download': 'تنزيل',
  'Edit': 'تعديل',
  'Export': 'تصدير',
  'Export CSV': 'تصدير CSV',
  'Import': 'استيراد',
  'Print': 'طباعة',
  'Register': 'تسجيل',
  'Reset': 'إعادة ضبط',
  'Reset Filters': 'إعادة ضبط عوامل التصفية',
  'Restore': 'استعادة',
  'Save': 'حفظ',
  'Saving...': 'جاري الحفظ...',
  'Search': 'بحث',
  'Template': 'القالب',
  'Update': 'تحديث',
  'Validate': 'اعتماد',
  'Actions': 'الإجراءات',
  'Action': 'الإجراء',
  'Action History': 'سجل الإجراءات',
  'Action Log': 'سجل الإجراءات',
  'Address': 'العنوان',
  'Amount': 'المبلغ',
  'Amount Range': 'نطاق المبلغ',
  'Barcode': 'الرمز الشريطي',
  'Category': 'الفئة',
  'Code': 'الرمز',
  'Color': 'اللون',
  'Created At': 'تاريخ الإنشاء',
  'Currency': 'العملة',
  'Date': 'التاريخ',
  'Description': 'الوصف',
  'End Date': 'تاريخ الانتهاء',
  'Expiry': 'انتهاء الصلاحية',
  'Expiry Date': 'تاريخ انتهاء الصلاحية',
  'Filters': 'عوامل التصفية',
  'From': 'من',
  'Formula': 'معادلة التحويل',
  'Label': 'البيان',
  'Max': 'الحد الأقصى',
  'Max Balance': 'الحد الأقصى للرصيد',
  'Min': 'الحد الأدنى',
  'Min Balance': 'الحد الأدنى للرصيد',
  'Name': 'الاسم',
  'Name *': 'الاسم *',
  'N/A': 'غير متاح',
  'Never': 'لم يُحتسب بعد',
  'No': 'لا',
  'None': 'لا شيء',
  'Note': 'ملاحظة',
  'Notes': 'ملاحظات',
  'Number': 'الرقم',
  'Optional': 'اختياري',
  'Options': 'الخيارات',
  'Price': 'السعر',
  'Progress': 'التقدم',
  'Quantity': 'الكمية',
  'Rate': 'المعدل',
  'Ratio': 'نسبة التحويل',
  'Reason / Notes': 'السبب / الملاحظات',
  'Reference': 'المرجع',
  'Reserved': 'المحجوز',
  'Source': 'المصدر',
  'Start Date': 'تاريخ البدء',
  'Status': 'الحالة',
  'Stock': 'المخزون',
  'Tags': 'الوسوم',
  'To': 'إلى',
  'Total': 'الإجمالي',
  'Type': 'النوع',
  'Type *': 'النوع *',
  'Value': 'القيمة',
  'Warehouse': 'المستودع',
  'Yes': 'نعم',
  'Active': 'نشط',
  'Inactive': 'غير نشط',
  'Draft': 'مسودة',
  'Posted': 'مُرحّل',
  'Pending': 'قيد الانتظار',
  'Completed': 'مكتمل',
  'Registered': 'مسجّل',
  'Disposed': 'مستبعد',
  'Void': 'ملغى',
  'Deleted': 'محذوف',
  'Reconciled': 'تمت تسويته',
  'Unreconciled': 'غير مسوّى',
  'Imported': 'مستورد',
  'Allowed': 'مسموح',
  'Restricted': 'مقيّد',
  'Healthy': 'سليم',
  'Overdrawn': 'مكشوف',
  'active': 'نشط',
  'inactive': 'غير نشط',
  'archived': 'مؤرشف',
  'deleted': 'محذوف',
  'draft': 'مسودة',
  'done': 'مكتمل',
  'pending': 'قيد الانتظار',
  'completed': 'مكتمل',
  'reconciled': 'تمت تسويته',
  'unreconciled': 'غير مسوّى',
  'reference': 'مرجعية',
  'bigger': 'أكبر',
  'smaller': 'أصغر',
  'sale': 'بيع',
  'purchase': 'شراء',
  'transfer': 'تحويل',
  'return': 'مرتجع',
  'all': 'الكل',
  'categories': 'الفئات',
  'warehouses': 'المستودعات',
  'lots': 'أرقام التشغيلات',
  'scrap': 'تالف',
  'contacts': 'جهات الاتصال',
  'sales': 'المبيعات',
  'purchases': 'المشتريات',
  'chart of accounts': 'دليل الحسابات',
  'tax base': 'قاعدة الضرائب',
  'default accounts': 'الحسابات الافتراضية',

  // Items and inventory manager
  'Items': 'الأصناف',
  'Manage your products and services': 'إدارة منتجاتك وخدماتك',
  'New Item': 'صنف جديد',
  'All Types': 'جميع الأنواع',
  'Product': 'منتج',
  'Products': 'المنتجات',
  'Service': 'خدمة',
  'Services': 'الخدمات',
  'Products & Services': 'المنتجات والخدمات',
  'Product Variant': 'متغير المنتج',
  'Product / Service': 'المنتج / الخدمة',
  'SKU / Code': 'رمز التخزين / الرمز',
  'SKU': 'رمز التخزين',
  'SKU:': 'رمز التخزين:',
  'SKU (Stock Keeping Unit) *': 'رمز وحدة حفظ المخزون (SKU) *',
  'On Hand': 'المتاح فعليًا',
  'Qty on Hand': 'الكمية المتاحة',
  'No items found.': 'لم يتم العثور على أصناف.',
  'No items found matching your criteria.': 'لم يتم العثور على أصناف تطابق معاييرك.',
  'Manage your stock, warehouses and movements': 'إدارة المخزون والمستودعات وحركات المخزون',
  'Inventory': 'المخزون',
  'Inventory Manager': 'إدارة المخزون',
  'Inventory Reports': 'تقارير المخزون',
  'All Items': 'جميع الأصناف',
  'More filters': 'مزيد من عوامل التصفية',
  'Less filters': 'عوامل تصفية أقل',
  'Search stock...': 'ابحث في المخزون...',
  'Product Categories': 'فئات المنتجات',
  'New Category': 'فئة جديدة',
  'Category Name *': 'اسم الفئة *',
  'No Category': 'بدون فئة',
  'No categories created yet.': 'لم يتم إنشاء فئات بعد.',
  'No products in this category': 'لا توجد منتجات في هذه الفئة',
  'Uncategorized Products': 'منتجات غير مصنفة',
  'Warehouses & Store Locations': 'المستودعات ومواقع المتاجر',
  'Warehouse Name *': 'اسم المستودع *',
  'No warehouses found. Click "Add New" to create one.': 'لا توجد مستودعات. انقر على «إضافة جديد» لإنشاء مستودع.',
  'Lot Number Registry': 'سجل أرقام التشغيلات',
  'Lot Numbers': 'أرقام التشغيلات',
  'Lot Number': 'رقم التشغيلة',
  'Lot Number *': 'رقم التشغيلة *',
  'Lot Number (Optional)': 'رقم التشغيلة (اختياري)',
  'Lot #': 'رقم التشغيلة',
  'New Lot': 'تشغيلة جديدة',
  'Add Lot': 'إضافة تشغيلة',
  'No Lot': 'بدون تشغيلة',
  'No lot numbers recorded.': 'لم تُسجل أرقام تشغيلات.',
  'No lots added. Click "Add Lot" to track by lot number.': 'لم تُضف تشغيلات. انقر على «إضافة تشغيلة» للتتبع برقم التشغيلة.',
  'Search Lot...': 'ابحث برقم التشغيلة...',
  'Enter Lot#': 'أدخل رقم التشغيلة',
  'Expiry Before': 'تنتهي قبل',
  'Lot Numbers & Expiry Dates': 'أرقام التشغيلات وتواريخ انتهاء الصلاحية',
  'Product Has Expiry': 'للمنتج تاريخ صلاحية',
  'This will toggle the product that it will have an expiry date that need to be tracked': 'يحدد هذا الخيار أن للمنتج تاريخ انتهاء صلاحية يجب تتبعه',
  'Scrap': 'التالف',
  'Scrap / Salvage': 'التالف / الاسترداد',
  'Scrap & Salvage Management': 'إدارة التالف والاسترداد',
  'Record New Scrap Action': 'تسجيل إجراء تالف جديد',
  'Source Product': 'المنتج المصدر',
  'Target Product (Result of salvage)': 'المنتج الناتج عن الاسترداد',
  'Write-off (Loss)': 'شطب (خسارة)',
  'Salvage (Transform)': 'استرداد (تحويل)',
  'Process Write-off': 'تنفيذ الشطب',
  'Process Salvage': 'تنفيذ الاسترداد',
  'Why are you scrapping this item?': 'ما سبب اعتبار هذا الصنف تالفًا؟',
  'Salvage Tip': 'إرشاد للاسترداد',
  'Salvaging allows you to transform one product into another. For example, if a table is broken, you might salvage the wood as raw materials. This reduces stock of the table and increases stock of the wood.': 'يتيح الاسترداد تحويل منتج إلى منتج آخر. فمثلًا، عند تلف طاولة يمكن استرداد الخشب كمواد خام، مما يخفض مخزون الطاولات ويزيد مخزون الخشب.',
  'Stock Implications': 'الأثر على المخزون',
  '• Stock will be reduced instantly': '• سيُخفض المخزون فورًا',
  '• A movement log will be created': '• سيُنشأ سجل لحركة المخزون',
  '• Cost of goods will be adjusted': '• ستُعدّل تكلفة البضاعة',
  'Stock Movement History': 'سجل حركات المخزون',
  'No movements recorded yet.': 'لم تُسجل حركات مخزون بعد.',
  'Movement Type': 'نوع الحركة',
  'Product Deliveries': 'تسليمات المنتجات',
  'Inventory by Lot/Warehouse': 'المخزون حسب التشغيلة/المستودع',
  'Transfer History': 'سجل التحويلات',
  'Operations (Transfers)': 'العمليات (التحويلات)',
  'Direct Delivery / Adjustment': 'تسليم مباشر / تسوية',
  'Stock Movement By Delivery': 'حركة المخزون حسب التسليم',
  'Delivery Ref #': 'مرجع التسليم',
  'Ref #': 'رقم المرجع',
  'Reference #': 'رقم المرجع',
  'History & Notes': 'السجل والملاحظات',
  'Total Items': 'إجمالي الأصناف',
  'Total Qty': 'إجمالي الكمية',
  'Total Value': 'إجمالي القيمة',
  'Inventory Report': 'تقرير المخزون',
  'All Products': 'جميع المنتجات',
  'All Warehouses': 'جميع المستودعات',
  'No stock records found.': 'لم يتم العثور على سجلات مخزون.',
  'No warehouse stocks defined. Add lines to set initial quantities.': 'لم تُحدد أرصدة للمستودعات. أضف بنودًا لتعيين الكميات الافتتاحية.',
  'Select Product': 'اختر منتجًا',
  'Select Product...': 'اختر منتجًا...',
  'Select Target Product': 'اختر المنتج الناتج',
  'Select Warehouse': 'اختر مستودعًا',
  'Select Location...': 'اختر موقعًا...',
  'Select Lot...': 'اختر تشغيلة...',
  '-- Manual Entry --': '-- إدخال يدوي --',
  'From Warehouse': 'من المستودع',
  'From Location (Source)': 'من الموقع (المصدر)',
  'To Location (Target)': 'إلى الموقع (الوجهة)',
  'Add Product': 'إضافة منتج',
  'Cost Price': 'سعر التكلفة',
  'Sale Price': 'سعر البيع',
  'Sale Price *': 'سعر البيع *',
  'Sell Price': 'سعر البيع',
  'Cost': 'التكلفة',
  'Sell': 'البيع',
  'Allow Negative': 'السماح بالسالب',
  'Allow Negative Stock': 'السماح بمخزون سالب',
  'This will allow the product to be sold in negative even with no stock': 'يسمح هذا الخيار ببيع المنتج حتى عند عدم توفر مخزون وتسجيل رصيد سالب',
  'Enable stock tracking (In/Out) for this product': 'تفعيل تتبع حركات الإدخال والإخراج لهذا المنتج',
  'Calculated from warehouse stocks': 'يُحتسب من أرصدة المستودعات',
  'This SKU already exists': 'رمز وحدة حفظ المخزون مستخدم بالفعل',
  'Detailed description...': 'وصف تفصيلي...',
  'Tax Rate': 'معدل الضريبة',
  'Tax Exempt (0%)': 'معفى من الضريبة (0٪)',
  'Enable POS': 'تفعيل نقطة البيع',
  'POS': 'نقطة البيع',
  'POS Options': 'خيارات نقطة البيع',
  'Make this item available in the Point of Sale system': 'إتاحة هذا الصنف في نظام نقطة البيع',
  'Hide From Sell Screen': 'إخفاء من شاشة البيع',
  'The product will be hidden to be sold in sell screen but will still be visible here in products and composite component': 'سيُخفى المنتج من شاشة البيع مع بقائه ظاهرًا ضمن المنتجات والمكونات المركبة',
  'Product Image': 'صورة المنتج',
  'Upload Image': 'رفع صورة',
  'Recommended size: 800x800px. Max 2MB.': 'الحجم الموصى به: 800×800 بكسل. الحد الأقصى 2 ميجابايت.',
  'Store Availability & Quantity': 'التوفر والكمية حسب المتجر',
  'Stock Quantity': 'كمية المخزون',
  'Recipe Management': 'إدارة الوصفات',
  '+ Add New Recipe': '+ إضافة وصفة جديدة',
  'Recipe Name (e.g. Standard, Premium)': 'اسم الوصفة (مثل: قياسية، مميزة)',
  'Components': 'المكونات',
  '+ Add Component': '+ إضافة مكوّن',
  'Select Item': 'اختر صنفًا',
  'Qty': 'الكمية',
  'Enable Variants': 'تفعيل المتغيرات',
  'Add options like size or color to create multiple versions of this product': 'أضف خيارات مثل المقاس أو اللون لإنشاء نسخ متعددة من المنتج',
  '+ Add Option': '+ إضافة خيار',
  'Option Name (e.g. Color, Size)': 'اسم الخيار (مثل: اللون، المقاس)',
  'Add value and press Enter...': 'أضف قيمة ثم اضغط Enter...',
  'Generate Variants': 'إنشاء المتغيرات',
  'Generated Variants': 'المتغيرات المنشأة',
  'Variant': 'المتغير',
  'Archive Item': 'أرشفة الصنف',
  'Update Item': 'تحديث الصنف',
  'Image uploaded successfully': 'تم رفع الصورة بنجاح',
  'Failed to upload image': 'تعذر رفع الصورة',
  'Item created successfully': 'تم إنشاء الصنف بنجاح',
  'Item updated successfully': 'تم تحديث الصنف بنجاح',
  'Item deleted successfully': 'تم حذف الصنف بنجاح',
  'Please fill in all required fields correctly.': 'يرجى تعبئة جميع الحقول المطلوبة بصورة صحيحة.',
  'Duplicate lot numbers are not allowed for the same product.': 'لا يُسمح بتكرار رقم التشغيلة للمنتج نفسه.',
  'Source product not found': 'لم يتم العثور على المنتج المصدر',
  'Product not found': 'لم يتم العثور على المنتج',
  'Insufficient stock for this operation': 'المخزون غير كافٍ لتنفيذ هذه العملية',
  'Item written off successfully': 'تم شطب الصنف بنجاح',
  'Item salvaged successfully': 'تم استرداد الصنف بنجاح',
  'Error processing scrap operation': 'حدث خطأ أثناء معالجة عملية التالف',
  'Unable to load items': 'تعذر تحميل الأصناف',
  'Unable to load lot numbers': 'تعذر تحميل أرقام التشغيلات',
  'Unable to save lot number': 'تعذر حفظ رقم التشغيلة',
  'Unable to update lot number': 'تعذر تحديث رقم التشغيلة',
  'Unable to create lot number': 'تعذر إنشاء رقم التشغيلة',
  'Unable to load inventory movements': 'تعذر تحميل حركات المخزون',
  'Error recording movement': 'حدث خطأ أثناء تسجيل حركة المخزون',
  'Movement recorded successfully': 'تم تسجيل حركة المخزون بنجاح',
  'Source and Target warehouses are required for transfer': 'يلزم تحديد مستودع المصدر ومستودع الوجهة للتحويل',
  'Warehouse is required': 'المستودع مطلوب',
  'Warehouse created successfully': 'تم إنشاء المستودع بنجاح',
  'Warehouse updated successfully': 'تم تحديث المستودع بنجاح',
  'Warehouse deleted': 'تم حذف المستودع',
  'Are you sure you want to delete this warehouse?': 'هل أنت متأكد من حذف هذا المستودع؟',
  'Lot number created': 'تم إنشاء رقم التشغيلة',
  'Lot number updated': 'تم تحديث رقم التشغيلة',
  'Lot number deleted': 'تم حذف رقم التشغيلة',
  'Delete this lot number?': 'هل تريد حذف رقم التشغيلة هذا؟',
  'Error deleting lot number': 'حدث خطأ أثناء حذف رقم التشغيلة',
  'Category created': 'تم إنشاء الفئة',
  'Category updated': 'تم تحديث الفئة',
  'Category deleted': 'تم حذف الفئة',
  'Delete this category? Products in this category will become uncategorized.': 'هل تريد حذف هذه الفئة؟ ستصبح المنتجات التابعة لها غير مصنفة.',
  'Error deleting category': 'حدث خطأ أثناء حذف الفئة',

  // Inventory locations, transfers, counts, replenishment, and quants
  'Inventory Locations': 'مواقع المخزون',
  'Manage physical and virtual stock locations': 'إدارة مواقع المخزون الفعلية والافتراضية',
  'New Location': 'موقع جديد',
  'Edit Location': 'تعديل الموقع',
  'Update Location': 'تحديث الموقع',
  'Save Location': 'حفظ الموقع',
  'Internal Location': 'موقع داخلي',
  'Customer Location': 'موقع العميل',
  'Vendor Location': 'موقع المورد',
  'Inventory Loss / Adj.': 'فاقد / تسوية مخزون',
  'Scrap Location': 'موقع التالف',
  'Transit Location': 'موقع قيد النقل',
  'None / Global': 'بدون / عام',
  'Optional barcode': 'رمز شريطي اختياري',
  'Search locations...': 'ابحث في المواقع...',
  'No locations found': 'لم يتم العثور على مواقع',
  'Create an inventory location to get started.': 'أنشئ موقع مخزون للبدء.',
  'Name is required': 'الاسم مطلوب',
  'Location created': 'تم إنشاء الموقع',
  'Location updated': 'تم تحديث الموقع',
  'Location deleted': 'تم حذف الموقع',
  'Error loading locations': 'حدث خطأ أثناء تحميل المواقع',
  'Error saving location': 'حدث خطأ أثناء حفظ الموقع',
  'Error deleting location': 'حدث خطأ أثناء حذف الموقع',
  'Transfers': 'التحويلات المخزنية',
  'Receipts, deliveries, and internal moves': 'الاستلامات والتسليمات والحركات الداخلية',
  'New Transfer': 'تحويل جديد',
  'Operation Type': 'نوع العملية',
  'Receipt': 'استلام',
  'Internal': 'داخلي',
  'Delivery': 'تسليم',
  'Source Location': 'موقع المصدر',
  'Destination Location': 'موقع الوجهة',
  'Operations (Moves)': 'العمليات (الحركات)',
  'Scan barcode...': 'امسح الرمز الشريطي...',
  'Demand': 'الكمية المطلوبة',
  'Done': 'الكمية المنفذة',
  '+ Add Line': '+ إضافة بند',
  'No moves in this transfer': 'لا توجد حركات في هذا التحويل',
  'Locations required': 'يجب تحديد المواقع',
  'Valid products and quantities are required': 'يجب إدخال منتجات وكميات صحيحة',
  'Transfer created': 'تم إنشاء التحويل',
  'Transfer updated': 'تم تحديث التحويل',
  'Transfer validated and stock moved': 'تم اعتماد التحويل ونقل المخزون',
  'Inventory Adjustments': 'تسويات المخزون',
  'Perform cycle counts and adjust stock balances': 'تنفيذ الجرد الدوري وتسوية أرصدة المخزون',
  'Current Stock By Location': 'المخزون الحالي حسب الموقع',
  'Location': 'الموقع',
  'Counted Qty': 'الكمية المعدودة',
  'Diff': 'الفرق',
  'No stock in any tracked locations': 'لا يوجد مخزون في المواقع المتتبعة',
  'No difference to apply': 'لا يوجد فرق لتطبيقه',
  'Count applied': 'تم تطبيق الجرد',
  'Failed to apply count': 'تعذر تطبيق الجرد',
  'An Inventory Loss type location is required in your Locations setup to post differences.': 'يلزم إعداد موقع من نوع «فاقد مخزون» لترحيل فروق الجرد.',
  'Replenishment': 'إعادة التزويد',
  'Manage low stock and automated reordering': 'إدارة انخفاض المخزون وإعادة الطلب تلقائيًا',
  'No Replenishment Rules': 'لا توجد قواعد لإعادة التزويد',
  "You haven't set up any min/max stock rules yet. Automated reordering will appear here once configured.": 'لم تُعدّ قواعد الحد الأدنى/الأقصى للمخزون بعد. ستظهر أوامر إعادة الطلب التلقائية هنا بعد الإعداد.',
  'Configure Reordering Rules': 'إعداد قواعد إعادة الطلب',
  'Planned Orders': 'الأوامر المخططة',
  'Generate purchase orders automatically when stock hits minimum thresholds.': 'إنشاء أوامر شراء تلقائيًا عند بلوغ المخزون الحد الأدنى.',
  'Stock Alerts': 'تنبيهات المخزون',
  'Get notified via email or dashboard alerts for products that need immediate attention.': 'تلقي إشعارات بالبريد الإلكتروني أو عبر لوحة القيادة للمنتجات التي تتطلب إجراءً فوريًا.',
  'Quantities on Hand (WMS)': 'الكميات المتاحة (نظام إدارة المستودعات)',

  // Inventory summary report
  'Back to Reports': 'العودة إلى التقارير',
  'Inventory Item Summary': 'ملخص أصناف المخزون',
  'As of': 'كما في',
  'Item': 'الصنف',
  'Unit Cost': 'تكلفة الوحدة',
  'No inventory data available': 'لا تتوفر بيانات مخزون',
  'Total Inventory Value': 'إجمالي قيمة المخزون',

  // Units of measure
  'UOM': 'وحدات القياس',
  'Units of Measure (UOM)': 'وحدات القياس (UOM)',
  'Define measurement units and their conversion ratios': 'تعريف وحدات القياس ونسب التحويل بينها',
  'Search UOMs...': 'ابحث في وحدات القياس...',
  'Create UOM': 'إنشاء وحدة قياس',
  'No UOMs found. Define your first measurement unit.': 'لم يتم العثور على وحدات قياس. عرّف أول وحدة قياس.',
  'Unit Name': 'اسم الوحدة',
  'Unit Type': 'نوع الوحدة',
  'Edit Unit of Measure': 'تعديل وحدة القياس',
  'Create New UOM': 'إنشاء وحدة قياس جديدة',
  '+ Create New Category': '+ إنشاء فئة جديدة',
  'Enter new category name...': 'أدخل اسم الفئة الجديدة...',
  'Ratio to Reference Unit': 'النسبة إلى الوحدة المرجعية',
  'Ratio to Reference Unit (Fraction)': 'النسبة إلى الوحدة المرجعية (كسر)',
  'Example: If 1 Dozen = 12 Units, Ratio is': 'مثال: إذا كان الدستة الواحدة = 12 وحدة، فإن نسبة التحويل هي',
  'Example: If 1000 Grams = 1 Kilogram, Ratio is': 'مثال: إذا كان 1000 جرام = 1 كيلوجرام، فإن نسبة التحويل هي',
  'Calculation: 1': 'الحساب: 1',
  'reference units': 'وحدة مرجعية',
  'Base': 'الوحدة الأساس',
  'Unit': 'وحدة',
  'Weight': 'وزن',
  'Length': 'طول',
  'Volume': 'حجم',
  'Time': 'زمن',
  'Update Unit': 'تحديث الوحدة',
  'Save Unit': 'حفظ الوحدة',
  'UOM created successfully': 'تم إنشاء وحدة القياس بنجاح',
  'UOM updated successfully': 'تم تحديث وحدة القياس بنجاح',
  'UOM deleted': 'تم حذف وحدة القياس',
  'Failed to load UOMs': 'تعذر تحميل وحدات القياس',
  'Failed to save UOM': 'تعذر حفظ وحدة القياس',
  'Failed to delete UOM': 'تعذر حذف وحدة القياس',
  'Could not load units of measure': 'تعذر تحميل وحدات القياس',
  'Could not save unit of measure': 'تعذر حفظ وحدة القياس',
  'Could not delete unit of measure': 'تعذر حذف وحدة القياس',
  'Are you sure you want to delete this UOM?': 'هل أنت متأكد من حذف وحدة القياس هذه؟',

  // Variant attributes
  'Product Variants': 'متغيرات المنتجات',
  'Manage global variant options like Size, Color, etc.': 'إدارة خيارات المتغيرات العامة مثل المقاس واللون وغيرها.',
  'Search variants...': 'ابحث في المتغيرات...',
  'Create Variant': 'إنشاء متغير',
  'Create Variant Group': 'إنشاء مجموعة متغيرات',
  'Edit Variant': 'تعديل المتغير',
  'Update Variant': 'تحديث المتغير',
  'Save Variant': 'حفظ المتغير',
  'No variant attributes found. Create "Size", "Color", etc.': 'لم يتم العثور على سمات متغيرات. أنشئ «المقاس» أو «اللون» ونحو ذلك.',
  'Variant Name': 'اسم المتغير',
  'Custom Variant': 'متغير مخصص',
  'No predefined options': 'لا توجد خيارات محددة مسبقًا',
  'Default Options (Press Enter to add)': 'الخيارات الافتراضية (اضغط Enter للإضافة)',
  'Type an option and press Enter...': 'اكتب خيارًا ثم اضغط Enter...',
  'Allow Custom Variants': 'السماح بمتغيرات مخصصة',
  'Permit staff to enter custom values for this attribute outside of the predefined list above.': 'السماح للموظفين بإدخال قيم مخصصة لهذه السمة خارج القائمة المحددة أعلاه.',
  'Attribute name is required': 'اسم السمة مطلوب',
  'Attribute created successfully': 'تم إنشاء السمة بنجاح',
  'Attribute updated successfully': 'تم تحديث السمة بنجاح',
  'Attribute deleted': 'تم حذف السمة',
  'Failed to load product variants': 'تعذر تحميل متغيرات المنتجات',
  'Failed to load product variants.': 'تعذر تحميل متغيرات المنتجات.',
  'Failed to save attribute': 'تعذر حفظ السمة',
  'Failed to delete attribute': 'تعذر حذف السمة',
  'Are you sure you want to delete this variant attribute?': 'هل أنت متأكد من حذف سمة المتغير هذه؟',

  // Accounting configuration and chart of accounts
  'Journals': 'دفاتر اليومية',
  'Chart of Accounts': 'دليل الحسابات',
  'Manage your ledger accounts and financial structure': 'إدارة حسابات الأستاذ والهيكل المالي',
  'Download Import Template': 'تنزيل قالب الاستيراد',
  'Seed Basic': 'تهيئة الحسابات الأساسية',
  'Add Account': 'إضافة حساب',
  'Add New Account': 'إضافة حساب جديد',
  'Search accounts...': 'ابحث في الحسابات...',
  'YTD': 'منذ بداية السنة',
  'YTD Balance': 'الرصيد منذ بداية السنة',
  'YTD Balance (Opening)': 'الرصيد الافتتاحي منذ بداية السنة',
  'No accounts found matching your criteria.': 'لم يتم العثور على حسابات تطابق معاييرك.',
  'Only for 0 balance': 'متاح فقط عند رصيد صفري',
  'No history recorded yet.': 'لم يُسجل أي سجل بعد.',
  'Restore Account': 'استعادة الحساب',
  'Restore to Active': 'استعادة إلى نشط',
  'Account Code *': 'رمز الحساب *',
  'Account Name *': 'اسم الحساب *',
  'Account Type *': 'نوع الحساب *',
  'This code is already in use.': 'رمز الحساب مستخدم بالفعل.',
  'Enable payments to this account': 'تفعيل المدفوعات على هذا الحساب',
  'Show on Dashboard Watchlist': 'إظهار في قائمة المراقبة بلوحة القيادة',
  'Save Account': 'حفظ الحساب',
  'Update Account': 'تحديث الحساب',
  'Edit Account': 'تعديل الحساب',
  'Account Guide': 'دليل الحسابات',
  'How account types affect your reports': 'كيفية تأثير أنواع الحسابات في التقارير',
  'Assets': 'الأصول',
  'Asset': 'الأصل',
  'Current Asset': 'أصل متداول',
  'Current Assets': 'الأصول المتداولة',
  'Fixed Asset': 'أصل ثابت',
  'Fixed Assets': 'الأصول الثابتة',
  'Non-current Asset': 'أصل غير متداول',
  'Non-current Assets': 'الأصول غير المتداولة',
  'Liability': 'الالتزامات',
  'Current Liability': 'التزام متداول',
  'Current Liabilities': 'الالتزامات المتداولة',
  'Non-current Liability': 'التزام غير متداول',
  'Non-current Liabilities': 'الالتزامات غير المتداولة',
  'Equity': 'حقوق الملكية',
  'Revenue': 'الإيرادات',
  'Income': 'الدخل',
  'Other Income': 'إيرادات أخرى',
  'Expenses': 'المصروفات',
  'Expense': 'مصروف',
  'Direct Cost': 'تكلفة مباشرة',
  'Direct Costs': 'التكاليف المباشرة',
  'Bank': 'البنك',
  'Cash': 'النقدية',
  'Debit': 'مدين',
  'Credit': 'دائن',
  'Nature': 'الطبيعة',
  'Profit & Loss': 'قائمة الأرباح والخسائر',
  'Less Cost of Sales': 'ناقص تكلفة المبيعات',
  'Gross Profit': 'مجمل الربح',
  'Plus Other Income': 'يضاف: إيرادات أخرى',
  'Less Expenses': 'ناقص المصروفات',
  'Depreciation': 'الإهلاك',
  'Overheads': 'المصروفات غير المباشرة',
  'Net Profit': 'صافي الربح',
  'Balance Sheet': 'قائمة المركز المالي',
  'Total Assets': 'إجمالي الأصول',
  'Less Liabilities': 'ناقص الالتزامات',
  'Net Assets': 'صافي الأصول',
  'Plus Net Profit': 'يضاف: صافي الربح',
  'Total Equity': 'إجمالي حقوق الملكية',
  'Net Profit from your Profit & Loss report automatically flows into the Equity section of your Balance Sheet.': 'يُرحّل صافي الربح من قائمة الأرباح والخسائر تلقائيًا إلى بند حقوق الملكية في قائمة المركز المالي.',
  'Main Cash Account': 'حساب النقدية الرئيسي',
  'Sales Revenue': 'إيرادات المبيعات',
  'Revenue from sales': 'الإيرادات الناتجة عن المبيعات',
  'Accounts imported and synchronized successfully': 'تم استيراد الحسابات ومزامنتها بنجاح',
  'An error occurred during import.': 'حدث خطأ أثناء الاستيراد.',
  'Failed to parse CSV file.': 'تعذر تحليل ملف CSV.',
  'Failed to import core accounts': 'تعذر استيراد الحسابات الأساسية',
  'Account created successfully': 'تم إنشاء الحساب بنجاح',
  'Account updated successfully': 'تم تحديث الحساب بنجاح',
  'Account created. Complete its bank profile from Bank Accounts.': 'تم إنشاء الحساب. أكمل بياناته البنكية من شاشة الحسابات البنكية.',
  'System accounts cannot be deleted as they are core to business processes.': 'لا يمكن حذف حسابات النظام لأنها أساسية لعمليات المنشأة.',
  'This account is currently set as a Default Account in Accounting Settings. Please re-assign its role before deleting.': 'هذا الحساب معيّن حاليًا كحساب افتراضي في الإعدادات المحاسبية. يرجى إعادة تعيين دوره قبل الحذف.',
  'Deletion blocked: This account has existing transactions. You should archive it instead to preserve financial history.': 'تعذر الحذف: لهذا الحساب معاملات مسجلة. يُنصح بأرشفته للحفاظ على السجل المالي.',
  'Created': 'تم الإنشاء',
  'Updated': 'تم التحديث',
  'Restored': 'تمت الاستعادة',
  'Unknown Time': 'وقت غير معروف',
  'Original Creator': 'المنشئ الأصلي',
  'No Email': 'لا يوجد بريد إلكتروني',

  // Accounting dashboard
  'Accounting Command Hub': 'مركز العمليات المحاسبية',
  'Live sync': 'مزامنة مباشرة',
  'Real-time journal ledgers, unpaid document pipelines, and treasury status': 'دفاتر يومية فورية، ومسارات المستندات غير المسددة، وحالة الخزينة',
  'New transaction': 'معاملة جديدة',
  'Customer Invoices': 'فواتير العملاء',
  'Sales Journal': 'دفتر يومية المبيعات',
  'Total Unpaid': 'إجمالي غير المسدد',
  'To Validate': 'بانتظار الاعتماد',
  'Awaiting Pay': 'بانتظار السداد',
  'Open Invoices': 'فتح الفواتير',
  'Vendor Bills': 'فواتير الموردين',
  'Purchase Journal': 'دفتر يومية المشتريات',
  'Total Outstanding': 'إجمالي الرصيد المستحق',
  'Bills to Pay': 'فواتير واجبة السداد',
  'Open Bills': 'فتح فواتير الموردين',
  'Miscellaneous Operations': 'عمليات متنوعة',
  'General Journal': 'دفتر اليومية العامة',
  'Draft Volume': 'قيمة المسودات',
  'Pending Draft Entries': 'قيود مسودة معلقة',
  'Draft Entries': 'قيود مسودة',
  'Journal Ledger': 'سجل القيود اليومية',
  'Ledger Balance': 'رصيد الأستاذ',
  'Reconcile & Statements': 'التسوية وكشوف الحساب',
  'Fixed Assets & Registers': 'الأصول الثابتة والسجلات',
  'Asset Depreciation': 'إهلاك الأصول',
  'Track equipment depreciation, asset classes, and annual straight-line allocation schedules.': 'تتبّع إهلاك المعدات وفئات الأصول وجداول التخصيص السنوي بطريقة القسط الثابت.',
  'Go to Fixed Assets': 'الانتقال إلى الأصول الثابتة',
  'Customer & Supplier Balances': 'أرصدة العملاء والموردين',
  'Sub-ledger Analytics': 'تحليلات الأستاذ المساعد',
  'Review aged receivables, payable statement of accounts, and partner ledger balances.': 'مراجعة أعمار الذمم المدينة وكشوف الذمم الدائنة وأرصدة حسابات الأطراف.',
  'Receivables': 'الذمم المدينة',
  'Payables': 'الذمم الدائنة',

  // Bank accounts and reconciliation
  'Bank Accounts': 'الحسابات البنكية',
  'Manage your business bank accounts and balances': 'إدارة الحسابات البنكية للمنشأة وأرصدتها',
  'Add Bank Account': 'إضافة حساب بنكي',
  'Edit Bank Account': 'تعديل الحساب البنكي',
  'Delete Bank Account': 'حذف الحساب البنكي',
  'Delete Account': 'حذف الحساب',
  'Search by account name or bank...': 'ابحث باسم الحساب أو البنك...',
  'Loading accounts...': 'جاري تحميل الحسابات...',
  'No bank accounts found': 'لم يتم العثور على حسابات بنكية',
  'Add your first account to start tracking balances': 'أضف أول حساب لبدء تتبع الأرصدة',
  'Current Balance': 'الرصيد الحالي',
  'Initial Balance': 'الرصيد الافتتاحي',
  'Reconciliation': 'التسوية البنكية',
  'Transactions': 'المعاملات',
  'Account Details': 'تفاصيل الحساب',
  'Account Name': 'اسم الحساب',
  'Bank Name': 'اسم البنك',
  'Account Number': 'رقم الحساب',
  'Account Nature': 'طبيعة الحساب',
  'Receive': 'مقبوضات',
  'Spend': 'مدفوعات',
  'No transactions found for this account': 'لا توجد معاملات لهذا الحساب',
  'Uncategorized': 'غير مصنف',
  'Bank Statement Reconciliation': 'تسوية كشف الحساب البنكي',
  'Import your bank statement to match transactions and reconcile your account.': 'استورد كشف الحساب البنكي لمطابقة المعاملات وتسوية الحساب.',
  'Import Statement': 'استيراد كشف الحساب',
  'Download CSV Template': 'تنزيل قالب CSV',
  'Local mode supports CSV statement imports': 'يدعم الوضع المحلي استيراد كشوف الحساب بصيغة CSV',
  'Supports OFX, QFX, QIF, QBO, and CSV': 'يدعم صيغ OFX وQFX وQIF وQBO وCSV',
  'Imported statements': 'كشوف الحساب المستوردة',
  'Reconciling:': 'جاري التسوية:',
  'Match bank statement lines (left) with system transactions (right)': 'طابق بنود كشف الحساب البنكي (يسارًا) مع معاملات النظام (يمينًا)',
  'matched': 'تمت مطابقتها',
  'Save & Close': 'حفظ وإغلاق',
  'Bank Statement Lines': 'بنود كشف الحساب البنكي',
  'No lines found for this statement': 'لا توجد بنود في كشف الحساب هذا',
  'Unmatch': 'إلغاء المطابقة',
  'System Transactions': 'معاملات النظام',
  'Ledger': 'الأستاذ',
  'Match Found': 'تم العثور على مطابقة',
  'Match': 'مطابقة',
  '+ Create New Transaction to Match': '+ إنشاء معاملة جديدة للمطابقة',
  'No posted ledger transaction has the same amount.': 'لا توجد معاملة مُرحّلة في الأستاذ بالمبلغ نفسه.',
  'Select a statement line to find a match.': 'اختر بندًا من كشف الحساب للبحث عن مطابقة.',
  'Create Account': 'إنشاء الحساب',
  'Statement imported successfully': 'تم استيراد كشف الحساب بنجاح',
  'Bank statement imported successfully': 'تم استيراد كشف الحساب البنكي بنجاح',
  'Failed to import statement': 'تعذر استيراد كشف الحساب',
  'Statement line reconciled': 'تمت تسوية بند كشف الحساب',
  'Failed to reconcile statement line': 'تعذرت تسوية بند كشف الحساب',
  'Statement line marked unreconciled': 'تم تعليم بند كشف الحساب كغير مسوّى',
  'Failed to unmatch statement line': 'تعذر إلغاء مطابقة بند كشف الحساب',
  'Bank account created successfully': 'تم إنشاء الحساب البنكي بنجاح',
  'Bank account updated successfully': 'تم تحديث الحساب البنكي بنجاح',
  'Bank account deleted successfully': 'تم حذف الحساب البنكي بنجاح',
  'Failed to save bank account': 'تعذر حفظ الحساب البنكي',
  'Failed to delete bank account': 'تعذر حذف الحساب البنكي',
  'Are you sure you want to delete this bank account? This action cannot be undone and all transaction history for this account will be affected.': 'هل أنت متأكد من حذف هذا الحساب البنكي؟ لا يمكن التراجع عن هذا الإجراء وسيتأثر سجل معاملات الحساب بالكامل.',

  // Fixed assets
  'Track and manage your organization\'s long-term assets': 'تتبّع وإدارة الأصول طويلة الأجل للمنشأة',
  'Run Depreciation': 'احتساب الإهلاك',
  'New Asset': 'أصل جديد',
  'New asset': 'أصل جديد',
  'Search assets...': 'ابحث في الأصول...',
  'Loading assets...': 'جاري تحميل الأصول...',
  'No assets found': 'لم يتم العثور على أصول',
  'Add your first fixed asset to start tracking depreciation': 'أضف أول أصل ثابت لبدء تتبع الإهلاك',
  'Purchased:': 'تاريخ الشراء:',
  'Cost: $': 'التكلفة: $',
  'Book Value: $': 'القيمة الدفترية: $',
  'Asset Details': 'تفاصيل الأصل',
  'Stores': 'المتاجر',
  'Serial Number': 'الرقم التسلسلي',
  'Purchase Information': 'بيانات الشراء',
  'Warranty Expiry': 'انتهاء الضمان',
  'Method': 'الطريقة',
  'Last Depreciated': 'آخر إهلاك',
  'Valuation': 'التقييم',
  'Accumulated Dep.': 'مجمع الإهلاك',
  'Book Value': 'القيمة الدفترية',
  'No description provided.': 'لم يُدخل وصف.',
  'Attachments': 'المرفقات',
  'Edit Asset': 'تعديل الأصل',
  'Register depreciation for a specific period': 'تسجيل الإهلاك لفترة محددة',
  'Depreciation Period (Up To)': 'فترة الإهلاك (حتى)',
  'This will calculate and post depreciation for all registered assets from their last depreciation date up to the selected date.': 'سيتم احتساب وترحيل الإهلاك لجميع الأصول المسجلة من تاريخ آخر إهلاك حتى التاريخ المحدد.',
  'Assets Pending': 'الأصول قيد الإهلاك',
  'Total Depreciation': 'إجمالي الإهلاك',
  'Assets Summary': 'ملخص الأصول',
  'Current Book Value': 'القيمة الدفترية الحالية',
  'To Depreciate': 'قيمة الإهلاك',
  'New Book Value': 'القيمة الدفترية الجديدة',
  'No assets found pending depreciation for this period.': 'لا توجد أصول مستحقة للإهلاك خلال هذه الفترة.',
  'Ensure your assets are reviewed before posting. This action updates the fixed assets register.': 'تأكد من مراجعة الأصول قبل الترحيل. سيحدّث هذا الإجراء سجل الأصول الثابتة.',
  'Post Depreciation': 'ترحيل الإهلاك',
  'Attach file': 'إرفاق ملف',
  'Asset name': 'اسم الأصل',
  'Asset number': 'رقم الأصل',
  'Purchase date': 'تاريخ الشراء',
  'Purchase price': 'سعر الشراء',
  '+ Add new asset type': '+ إضافة نوع أصل جديد',
  'Asset type': 'نوع الأصل',
  'Select an asset type': 'اختر نوع أصل',
  'Computer Equipment': 'معدات حاسب آلي',
  'Medical Equipment': 'معدات طبية',
  'Office Equipment': 'معدات مكتبية',
  'Furniture': 'أثاث',
  'Vehicles': 'مركبات',
  'Warranty expiry': 'انتهاء الضمان',
  'Serial number': 'الرقم التسلسلي',
  'Select a store': 'اختر متجرًا',
  'Select a type': 'اختر نوعًا',
  'Fixed': 'ملموس',
  'Intangible': 'غير ملموس',
  'Book depreciation settings': 'إعدادات الإهلاك الدفتري',
  'Depreciation start date': 'تاريخ بدء الإهلاك',
  'Depreciation method': 'طريقة الإهلاك',
  'Select a depreciation method': 'اختر طريقة الإهلاك',
  'Straight Line': 'القسط الثابت',
  'Declining Balance': 'الرصيد المتناقص',
  'Full Depreciation': 'الإهلاك الكامل',
  'No Depreciation': 'بدون إهلاك',
  'Averaging method': 'طريقة احتساب الفترة',
  'The actual days': 'الأيام الفعلية',
  'Full month': 'شهر كامل',
  'Calculate depreciation based on': 'احتساب الإهلاك بناءً على',
  'Effective life': 'العمر الإنتاجي',
  'Effective Life': 'العمر الإنتاجي',
  'Depreciation rate (%)': 'معدل الإهلاك (٪)',
  'Effective life (Years)': 'العمر الإنتاجي (بالسنوات)',
  'Cost limit': 'حد التكلفة',
  'Residual value': 'القيمة التخريدية',
  'Save as draft': 'حفظ كمسودة',
  'New asset type': 'نوع أصل جديد',
  'Account type (Name)': 'نوع الحساب (الاسم)',
  'Asset account': 'حساب الأصل',
  'Select account': 'اختر حسابًا',
  'Accumulated depreciation account': 'حساب مجمع الإهلاك',
  'Depreciation account': 'حساب مصروف الإهلاك',
  'Basis': 'أساس الاحتساب',
  'Delete Asset': 'حذف الأصل',
  'Are you sure you want to delete this asset? This action cannot be undone.': 'هل أنت متأكد من حذف هذا الأصل؟ لا يمكن التراجع عن هذا الإجراء.',
  'Asset created': 'تم إنشاء الأصل',
  'Asset updated': 'تم تحديث الأصل',
  'Asset deleted': 'تم حذف الأصل',
  'Asset type created': 'تم إنشاء نوع الأصل',
  'Failed to save asset': 'تعذر حفظ الأصل',
  'Failed to delete asset': 'تعذر حذف الأصل',
  'Failed to save asset type': 'تعذر حفظ نوع الأصل',
  'No assets pending depreciation for this period': 'لا توجد أصول مستحقة للإهلاك خلال هذه الفترة',
  'Failed to post depreciation': 'تعذر ترحيل الإهلاك',

  // Manual journals and journal setup
  'Manual Journals': 'القيود اليومية اليدوية',
  'Create and manage manual ledger adjustments': 'إنشاء وإدارة تسويات الأستاذ اليدوية',
  'New Journal': 'قيد جديد',
  'New Manual Journal': 'قيد يومية يدوي جديد',
  'Search journals...': 'ابحث في القيود...',
  'Loading journals...': 'جاري تحميل القيود...',
  'No journals found': 'لم يتم العثور على قيود',
  'No journals found.': 'لم يتم العثور على دفاتر يومية.',
  'Create your first manual journal entry': 'أنشئ أول قيد يومية يدوي',
  'Narration': 'البيان',
  'Journal:': 'القيد:',
  'No narration provided.': 'لم يُدخل بيان للقيد.',
  'Total Balance': 'إجمالي الرصيد',
  'Journal Lines': 'بنود القيد',
  'Partner': 'الطرف',
  'Reverse Journal': 'عكس القيد',
  'Edit Journal': 'تعديل القيد',
  'Reference (Auto)': 'المرجع (تلقائي)',
  'Journal Book': 'دفتر اليومية',
  'Select Journal Type...': 'اختر نوع دفتر اليومية...',
  'Accounting Date': 'التاريخ المحاسبي',
  'Journal': 'دفتر اليومية',
  'Journal Items': 'بنود القيد',
  'Add Line': 'إضافة بند',
  'Account Name or Code': 'اسم الحساب أو رمزه',
  'Select Partner...': 'اختر الطرف...',
  'Out of balance by $': 'فرق عدم التوازن $',
  'Save as Draft': 'حفظ كمسودة',
  'Update Journal': 'تحديث القيد',
  'Post Journal': 'ترحيل القيد',
  'Delete Journal': 'حذف القيد',
  'Are you sure you want to delete this journal entry? This action cannot be undone.': 'هل أنت متأكد من حذف قيد اليومية هذا؟ لا يمكن التراجع عن هذا الإجراء.',
  'Journal must be balanced (Total Debit must equal Total Credit)': 'يجب أن يكون القيد متوازنًا (إجمالي المدين يساوي إجمالي الدائن)',
  'Journal amount cannot be zero': 'لا يمكن أن يكون مبلغ القيد صفرًا',
  'Please select an account for all lines': 'يرجى اختيار حساب لجميع البنود',
  'Journal posted': 'تم ترحيل القيد',
  'Journal saved as draft': 'تم حفظ القيد كمسودة',
  'Journal updated': 'تم تحديث القيد',
  'Failed to save journal': 'تعذر حفظ القيد',
  'Journal reversed': 'تم عكس القيد',
  'Failed to reverse journal': 'تعذر عكس القيد',
  'Journal deleted': 'تم حذف القيد',
  'Failed to delete journal': 'تعذر حذف القيد',
  'Account created': 'تم إنشاء الحساب',
  'Journals Setup': 'إعداد دفاتر اليومية',
  'Manage accounting journals': 'إدارة دفاتر اليومية المحاسبية',
  'Add Journal': 'إضافة دفتر يومية',
  'Create a new journal book': 'إنشاء دفتر يومية جديد',
  'Journal Name': 'اسم دفتر اليومية',
  'Short Code': 'الرمز المختصر',
  'Are you sure you want to delete this journal?': 'هل أنت متأكد من حذف دفتر اليومية هذا؟',
  'Failed to load accounting journals': 'تعذر تحميل دفاتر اليومية المحاسبية',
  'Failed to create accounting journal': 'تعذر إنشاء دفتر اليومية المحاسبي',
  'Failed to delete accounting journal': 'تعذر حذف دفتر اليومية المحاسبي',
  'Journal created successfully': 'تم إنشاء دفتر اليومية بنجاح',
  'Journal deleted successfully': 'تم حذف دفتر اليومية بنجاح',

  // Default accounts
  'Financial Core': 'النواة المالية',
  'Default Accounts': 'الحسابات الافتراضية',
  'Map automated system actions to your general ledger': 'ربط إجراءات النظام التلقائية بحسابات الأستاذ العام',
  'Seed Missing Accounts': 'تهيئة الحسابات الناقصة',
  'Sync & Save': 'مزامنة وحفظ',
  'Syncing Ledgers...': 'جاري مزامنة دفاتر الأستاذ...',
  '-- NO ACCOUNT MAPPED --': '-- لا يوجد حساب مربوط --',
  'No active accounts found': 'لم يتم العثور على حسابات نشطة',
  'You need to have active accounts in your Chart of Accounts before they can be mapped here.': 'يجب توفر حسابات نشطة في دليل الحسابات قبل ربطها هنا.',
  'Initialize System Accounts': 'تهيئة حسابات النظام',
  'Sales Account': 'حساب المبيعات',
  'Accounts Receivable': 'حساب الذمم المدينة',
  'Accounts Payable': 'حساب الذمم الدائنة',
  'Inventory Account': 'حساب المخزون',
  'Cost of Goods Sold (COGS)': 'حساب تكلفة البضاعة المباعة',
  'Purchase Account': 'حساب المشتريات',
  'VAT Input (Purchases)': 'ضريبة القيمة المضافة المدخلة (المشتريات)',
  'VAT Output (Sales)': 'ضريبة القيمة المضافة المخرجة (المبيعات)',
  'VAT Payable/Settlement': 'ضريبة القيمة المضافة المستحقة/التسوية',
  'Default Bank/Cash Account': 'حساب البنك/النقدية الافتراضي',
  'Secondary Bank Account': 'حساب البنك الثانوي',
  'Secondary Cash Account': 'حساب النقدية الثانوي',
  'Sales Returns Account': 'حساب مردودات المبيعات',
  'Purchase Returns Account': 'حساب مردودات المشتريات',
  'Discount Given Account': 'حساب الخصم الممنوح',
  'Discount Received Account': 'حساب الخصم المكتسب',
  'Delivery In Account': 'حساب مصروف نقل المشتريات',
  'Delivery Out Account': 'حساب مصروف توصيل المبيعات',
  'System accounts seeded successfully': 'تمت تهيئة حسابات النظام بنجاح',
  'Failed to seed accounts': 'تعذرت تهيئة الحسابات',
  'Failed to load default accounts': 'تعذر تحميل الحسابات الافتراضية',
  'Default accounts saved successfully': 'تم حفظ الحسابات الافتراضية بنجاح',
  'Failed to save default accounts': 'تعذر حفظ الحسابات الافتراضية',

  // Tags
  'Tags Setup': 'إعداد الوسوم',
  'Manage tags to categorize your data': 'إدارة الوسوم لتصنيف البيانات',
  'New Tag': 'وسم جديد',
  'Edit Tag': 'تعديل الوسم',
  'Create a new classification tag': 'إنشاء وسم تصنيفي جديد',
  'Search Tags': 'البحث في الوسوم',
  'Search by tag name...': 'ابحث باسم الوسم...',
  'Total Tags': 'إجمالي الوسوم',
  'Tag Name': 'اسم الوسم',
  'Tag Color': 'لون الوسم',
  'No tags found.': 'لم يتم العثور على وسوم.',
  'Confirm Delete': 'تأكيد الحذف',
  'Are you sure you want to delete this tag? This action cannot be undone.': 'هل أنت متأكد من حذف هذا الوسم؟ لا يمكن التراجع عن هذا الإجراء.',
  'Tag name is required': 'اسم الوسم مطلوب',
  'Tag added': 'تمت إضافة الوسم',
  'Tag updated': 'تم تحديث الوسم',
  'Tag deleted': 'تم حذف الوسم',
  'Failed to load tags': 'تعذر تحميل الوسوم',
  'Failed to save tag': 'تعذر حفظ الوسم',
  'Failed to delete tag': 'تعذر حذف الوسم',
  'Gray': 'رمادي',
  'Red': 'أحمر',
  'Amber': 'كهرماني',
  'Emerald': 'زمردي',
  'Blue': 'أزرق',
  'Indigo': 'نيلي',
  'Violet': 'بنفسجي',
  'Pink': 'وردي',
  'Rose': 'وردي داكن',

  // VAT base
  'VAT Base': 'قاعدة ضريبة القيمة المضافة',
  'Manage VAT rates used across the system': 'إدارة معدلات ضريبة القيمة المضافة المستخدمة في النظام',
  'Add New VAT Rate': 'إضافة معدل ضريبة جديد',
  'Search by name or rate...': 'ابحث بالاسم أو المعدل...',
  'Mapped Account': 'الحساب المرتبط',
  'No VAT rates found': 'لم يتم العثور على معدلات ضريبة قيمة مضافة',
  'Edit VAT Rate': 'تعديل معدل الضريبة',
  'Add VAT Rate': 'إضافة معدل ضريبة',
  'Rate (%) *': 'المعدل (٪) *',
  'Additional details...': 'تفاصيل إضافية...',
  'Mapped Tax Account': 'حساب الضريبة المرتبط',
  'No account mapped': 'لا يوجد حساب مرتبط',
  'Sales & Purchases': 'المبيعات والمشتريات',
  'Sales Only': 'المبيعات فقط',
  'Purchases Only': 'المشتريات فقط',
  'Both': 'كلاهما',
  'VAT 15%': 'ضريبة قيمة مضافة 15٪',
  'VAT 0%': 'ضريبة قيمة مضافة 0٪',
  'Standard VAT Rate': 'معدل ضريبة القيمة المضافة القياسي',
  'Zero Rated VAT': 'ضريبة قيمة مضافة بنسبة صفر',
  'Exempt': 'معفى',
  'Tax Exempt': 'معفى من الضريبة',
  'Chart of Accounts initialized': 'تمت تهيئة دليل الحسابات',
  'Standard tax rates initialized': 'تمت تهيئة معدلات الضرائب القياسية',
  'Failed to seed initial data': 'تعذرت تهيئة البيانات الأولية',
  'Failed to load tax rates': 'تعذر تحميل معدلات الضرائب',
  'Failed to load chart of accounts': 'تعذر تحميل دليل الحسابات',
  'Please fill in all required fields': 'يرجى تعبئة جميع الحقول المطلوبة',
  'Tax rate updated successfully': 'تم تحديث معدل الضريبة بنجاح',
  'Tax rate added successfully': 'تمت إضافة معدل الضريبة بنجاح',
  'Tax rate deleted successfully': 'تم حذف معدل الضريبة بنجاح',
  'Failed to save tax rate': 'تعذر حفظ معدل الضريبة',
  'Failed to delete tax rate': 'تعذر حذف معدل الضريبة',
  'Are you sure you want to delete this tax rate?': 'هل أنت متأكد من حذف معدل الضريبة هذا؟',

  // Customer and vendor balances
  'Customer Balances': 'أرصدة العملاء',
  'Vendor Balances': 'أرصدة الموردين',
  'Manage and track accounts receivable by customer.': 'إدارة وتتبع الذمم المدينة حسب العميل.',
  'Manage and track accounts payable by vendor.': 'إدارة وتتبع الذمم الدائنة حسب المورد.',
  'Search customers...': 'ابحث في العملاء...',
  'Search vendors...': 'ابحث في الموردين...',
  'Clear Filters': 'مسح عوامل التصفية',
  'Loading matches...': 'جاري تحميل النتائج...',
  'Customer Name': 'اسم العميل',
  'Vendor Name': 'اسم المورد',
  'Quoted (Total)': 'إجمالي عروض الأسعار',
  'Invoiced (Total)': 'إجمالي الفواتير',
  'Ordered (POs)': 'إجمالي أوامر الشراء',
  'Billed (Total)': 'إجمالي فواتير الموردين',
  'Paid': 'المسدّد',
  'Adjustments (Journals)': 'التسويات (القيود اليومية)',
  'Balance Due': 'الرصيد المستحق',
  'No records found.': 'لم يتم العثور على سجلات.',
  'Customer': 'العميل',
  'Vendor': 'المورد',

  // Trial balance and year-end closing
  'Trial Balance': 'ميزان المراجعة',
  'Financial state of your accounts between': 'الحالة المالية للحسابات خلال الفترة من',
  'and': 'إلى',
  'to': 'إلى',
  'Year-End Closing': 'إقفال نهاية السنة',
  'Filter by name or code...': 'صفِّ حسب الاسم أو الرمز...',
  'Non-Zero': 'غير الصفري',
  'Zero Only': 'الصفري فقط',
  'Ending Balance': 'الرصيد الختامي',
  'Auditing accounts...': 'جاري تدقيق الحسابات...',
  'Positive balance indicates Debit': 'الرصيد الموجب يشير إلى رصيد مدين',
  'Negative balance indicates Credit': 'الرصيد السالب يشير إلى رصيد دائن',
  'Values are in base currency': 'القيم معروضة بالعملة الأساسية',
  'This process will transfer all balances from': 'ستنقل هذه العملية جميع أرصدة',
  'accounts to the': 'إلى حساب',
  'Retained Earnings': 'الأرباح المحتجزة',
  'account, effectively zeroing out the P&L for the new financial year.': '، مما يؤدي إلى تصفير حسابات الأرباح والخسائر للسنة المالية الجديدة.',
  'Closing Date': 'تاريخ الإقفال',
  'Permanent Action:': 'إجراء نهائي:',
  'This will create a posted manual journal entry. Ensure all transactions for the year have been recorded before proceeding.': 'سينشئ هذا الإجراء قيد يومية يدويًا مُرحّلًا. تأكد من تسجيل جميع معاملات السنة قبل المتابعة.',
  'Confirm Closing': 'تأكيد الإقفال',
  'Suspense/Unmapped Account': 'حساب معلّق/غير مربوط',
  'Retained Earnings account not found. Creating it...': 'لم يتم العثور على حساب الأرباح المحتجزة. جاري إنشاؤه...',
  'No balances found in P&L accounts to close.': 'لا توجد أرصدة في حسابات الأرباح والخسائر لإقفالها.',
  'Year-End Closing completed successfully!': 'اكتمل إقفال نهاية السنة بنجاح!',
  'Failed to perform year-end closing.': 'تعذر تنفيذ إقفال نهاية السنة.',
};

export const inventoryAccountingPatterns: Array<[RegExp, (...matches: string[]) => string]> = [
  // Generic error wrappers preserve the original API/user-facing error detail.
  [/^Error:\s*(.+)$/i, detail => `خطأ: ${detail}`],
  [/^Request failed \((.+)\)$/i, status => `فشل الطلب (${status})`],

  // Inventory dynamic messages and labels.
  [/^Insufficient stock for (.+) in source warehouse$/i, item => `المخزون غير كافٍ للصنف ${item} في مستودع المصدر`],
  [/^Item (archived|restored) successfully$/i, state => `تم ${state.toLowerCase() === 'archived' ? 'أرشفة' : 'استعادة'} الصنف بنجاح`],
  [/^Barcode (.+) not found in catalog\.$/i, code => `لم يتم العثور على الرمز الشريطي ${code} في دليل الأصناف.`],
  [/^Scanned (.+) \(Total: (.+)\)$/i, (product, total) => `تم مسح ${product} (الإجمالي: ${total})`],
  [/^Added (.+) to transfer$/i, product => `تمت إضافة ${product} إلى التحويل`],
  [/^(.+) \(Warehouse\)$/i, name => `${name} (مستودع)`],
  [/^(.+) \(Store\)$/i, name => `${name} (متجر)`],

  // Dates and report periods preserve the formatted date values.
  [/^As of\s+(.+)$/i, date => `كما في ${date}`],
  [/^Financial state of your accounts between\s+(.+)\s+and\s+(.+)$/i, (start, end) => `الحالة المالية للحسابات خلال الفترة من ${start} إلى ${end}`],
  [/^Year-End Closing for period through\s+(.+)$/i, date => `إقفال نهاية السنة للفترة المنتهية في ${date}`],
  [/^Year-End Closing\s+(.+)$/i, date => `إقفال نهاية السنة ${date}`],
  [/^Year-End Net Result\s+(.+)$/i, date => `صافي نتيجة نهاية السنة ${date}`],

  // Accounting dashboard and accessibility labels preserve card/user data.
  [/^More options for\s+(.+)$/i, title => `خيارات إضافية لـ ${title}`],
  [/^(\d+) Draft Entries$/i, count => `${count} قيد مسودة`],
  [/^Bank Account •\s*(.+)$/i, accountNumber => `حساب بنكي • ${accountNumber}`],
  [/^Bank account:\s*(.+)$/i, bankName => `حساب بنكي: ${bankName}`],

  // Bank statement import and reconciliation.
  [/^Imported (\d+) statement line$/i, count => `تم استيراد ${count} بند من كشف الحساب`],
  [/^Imported (\d+) statement lines$/i, count => `تم استيراد ${count} بندًا من كشف الحساب`],
  [/^Reconciling:\s*(.+)$/i, fileName => `جاري تسوية: ${fileName}`],
  [/^(\d+)\s*\/\s*(\d+) matched$/i, (matched, total) => `تمت مطابقة ${matched} من ${total}`],

  // Fixed assets preserve asset names, dates, amounts, and currency symbols/codes.
  [/^Purchased:\s*(.+)$/i, date => `تاريخ الشراء: ${date}`],
  [/^Cost:\s*(.+)$/i, amount => `التكلفة: ${amount}`],
  [/^Book Value:\s*(.+)$/i, amount => `القيمة الدفترية: ${amount}`],
  [/^Edit asset:\s*(.+)$/i, assetName => `تعديل الأصل: ${assetName}`],
  [/^Depreciation posted for (\d+) assets$/i, count => `تم ترحيل الإهلاك لـ ${count} أصل`],

  // Journal/account patterns preserve references, amounts, rates, and names.
  [/^Journal:\s*(.+)$/i, reference => `القيد: ${reference}`],
  [/^Edit Journal:\s*(.+)$/i, reference => `تعديل القيد: ${reference}`],
  [/^Out of balance by\s*(.+)$/i, amount => `فرق عدم التوازن ${amount}`],
  [/^Tax \((.+)%\)$/i, rate => `ضريبة (${rate}٪)`],
  [/^(.+) \((.+)%\)$/i, (taxName, rate) => `${taxName} (${rate}٪)`],
  [/^Import complete:\s*(\d+) accounts added,\s*(\d+) skipped\.$/i, (added, skipped) => `اكتمل الاستيراد: أُضيف ${added} حساب، وتم تجاوز ${skipped}.`],
  [/^Account (deleted|archived|restored) successfully$/i, state => {
    const translated = state.toLowerCase() === 'deleted' ? 'حذف' : state.toLowerCase() === 'archived' ? 'أرشفة' : 'استعادة';
    return `تم ${translated} الحساب بنجاح`;
  }],
  [/^(Created|Restored|Updated|Archived|Deleted) by:$/i, action => {
    const translated: Record<string, string> = {
      created: 'تم الإنشاء',
      restored: 'تمت الاستعادة',
      updated: 'تم التحديث',
      archived: 'تمت الأرشفة',
      deleted: 'تم الحذف',
    };
    return `${translated[action.toLowerCase()] || action} بواسطة:`;
  }],

  // Unit conversion formulas preserve unit names and numeric ratios.
  [/^1 (.+) = Base$/i, unit => `1 ${unit} = الوحدة الأساس`],
  [/^1 (.+) = (.+) (.+)$/i, (unit, ratio, referenceUnit) => `1 ${unit} = ${ratio} ${referenceUnit}`],
  [/^Calculation:\s*1 (.+) = (.+) reference units$/i, (unit, ratio) => `الحساب: 1 ${unit} = ${ratio} وحدة مرجعية`],
];
